import { useEffect } from "react";
import { I18nProvider, OverlayProvider } from "react-aria";
import { useTranslation } from "react-i18next";
import type { PluginConfiguration } from "@pexip-engage-public/plugin-configuration";
import { parseFromSearchParamConfig } from "@pexip-engage-public/plugin-configuration-parser";
import { json, LinksFunction, LoaderFunctionArgs, SerializeFrom } from "@remix-run/node";
import {
  Links,
  LiveReload,
  Meta,
  Outlet,
  Scripts,
  type ShouldRevalidateFunction,
  useLoaderData,
  useRouteLoaderData } from
"@remix-run/react";
import { withSentry } from "@sentry/remix";
import * as Sentry from "@sentry/remix";
import { ExternalScripts } from "remix-utils/external-scripts";

import tailwindStyles from "~/styles/tailwind.css";
import variablesStyles from "~/styles/variables.css";
import remixImageStyles from "remix-image/remix-image.css";

import { AppErrorBoundary } from "~/components/AppErrorBoundary";
import { IFrameSetup } from "~/components/IFrameSetup";
import { MediaQueryProvider } from "~/components/Media";
import { getEnv } from "~/env.server";
import { i18n } from "~/i18n.server";
import { ServerStateManager } from "~/modules/StateManager.server";
import { getGoogleMapsKey } from "~/services/infra-configuration.server";
import { redisCache } from "~/services/redis.server";
import { useNonce } from "~/utils/nonce-provider";
import { ALL_TIMEZONES } from "~/utils/timezones.utils";

export const links: LinksFunction = () => [
// Preload CSS as a resource to avoid render blocking
{ rel: "preload", href: variablesStyles, as: "style" },
{ rel: "preload", href: tailwindStyles, as: "style" },
{ rel: "preload", href: remixImageStyles, as: "style" },
{ rel: "icon", type: "image/png", href: "/favicon.png" },
{ rel: "apple-touch-icon", href: "/apple-touch-icon.png" }, (
{
  rel: "manifest",
  href: "/manifest.json",
  crossOrigin: "use-credentials"
} as const), // necessary to make typescript happy
{ rel: "stylesheet", href: variablesStyles },
{ rel: "stylesheet", href: tailwindStyles },
{ rel: "stylesheet", href: remixImageStyles }];


/**
 * We use this to resolve locale in root, because it might not have been saved yet by the index loader
 * This happens when we throw an error for non schedulability.
 * So we attempt to resolve language from manager -> config -> request.
 *
 **/
async function resolveLocale(args: LoaderFunctionArgs) {
  const serverManager = new ServerStateManager({
    ...args,
    searchParams: await redisCache.getSession(args.context.sessionId)
  });

  if (serverManager.state.config.language) return serverManager.state.config.language;

  const url = new URL(args.request.url);
  const configuration = (parseFromSearchParamConfig(url.searchParams) as PluginConfiguration | null);

  if (configuration?.language) return configuration.language;

  return i18n.getLocale(args.request);
}

export async function loader(args: LoaderFunctionArgs) {
  return args.context.measurer.time("root", async function rootLoader() {
    const locale = await resolveLocale(args);

    return json({
      locale,
      sessionId: args.context.sessionId,
      ENV: getEnv(),
      GOOGLE_MAPS_API_KEY: getGoogleMapsKey(),
      SERVER_TIME_ZONES: ALL_TIMEZONES
    });
  });
}

export function useRootLoaderData(): SerializeFrom<typeof loader> | undefined {
  return useRouteLoaderData<typeof loader>("root");
}

export const shouldRevalidate: ShouldRevalidateFunction = () => false;

export let handle = {
  // In the handle export, we could add a i18n key with namespaces our route
  // will need to load. This key can be a single string or an array of strings.
  i18n: ["common"]
};

interface DocumentProps {
  children: React.ReactNode;
  nonce: string;
  env?: Record<string, unknown>;
}

function Document({ children, env = {}, nonce }: DocumentProps) {
  let { i18n } = useTranslation();

  return (
    <html dir={i18n.dir()} lang={i18n.language}>
      <head>
        <title>Pexip Engage Plugin</title>
        <meta charSet="utf-8" />
        <meta
          content="Pexip Engage Plugin allows you to book meetings efficiently and effectively."
          name="description" />

        <meta content="width=device-width,initial-scale=1" name="viewport" />
        <Links />
        <Meta />
      </head>
      <body className="bg-body p-xs text-regular">
        <OverlayProvider>
          {/* This is causing global react hydration warning. Awaiting fixes for react aria SSR support */}
          <I18nProvider locale={i18n.language}>
            <MediaQueryProvider>{children}</MediaQueryProvider>
          </I18nProvider>
        </OverlayProvider>
        {/* Breaks Chrome in Incognito, due to sessionStorage access */}
        {/* <ScrollRestoration nonce={nonce} /> */}
        <script
          dangerouslySetInnerHTML={{
            __html: `window.ENV = ${JSON.stringify(env)}`
          }}
          nonce={nonce} />

        <IFrameSetup />
        <ExternalScripts />
        <Scripts nonce={nonce} />
        <LiveReload nonce={nonce} />
        <div className="relative">
          <div className="absolute top-sm" data-iframe-height></div>
        </div>
      </body>
    </html>);

}

function App() {
  // Get the locale from the loader
  const { ENV, locale, sessionId } = useLoaderData<typeof loader>();
  const nonce = useNonce();

  useEffect(() => {
    Sentry.setUser({ id: sessionId });
  }, [locale, sessionId]);

  // This hook will change the i18n instance language to the current locale
  // detected by the loader, this way, when we do something to change the
  // language, this locale will change and i18next will load the correct
  // translation files
  useChangeLanguage(locale);

  return (
    <Document env={ENV} nonce={nonce}>
      <div data-component="AppContainer">
        <Outlet />
      </div>
    </Document>);

}

export default withSentry(App);

export function ErrorBoundary() {
  const nonce = useNonce();

  return (
    <Document nonce={nonce}>
      <AppErrorBoundary />
    </Document>);

}

/**
 * Workaround for https://github.com/sergiodxa/remix-i18next/issues/107
 */
function useChangeLanguage(locale: string) {
  let { i18n } = useTranslation();
  useEffect(() => {
    i18n.changeLanguage(locale);
    /* eslint-disable-next-line */
  }, []);
}